import type { QueryClient } from "@tanstack/react-query";

/**
 * Single source of truth for the React Query cache-key shape used by
 * `EventWebsitePage`. Every mutation that touches the website-config
 * surface (delete, toggle, upload, page add/duplicate/delete, custom-
 * domain edit) should invalidate THESE keys so the page's `useQuery`
 * hooks refetch.
 *
 * 2026-05-16 (audit CRIT-4 + HI-6): pre-fix, `WebsiteCard`,
 * `WebsiteDelete`, and `PageActions` each maintained their own
 * `invalidateQueries` block, and three of those targeted the legacy
 * `["websiteEditor", eventId]` / `["websiteConfigExists", eventId]`
 * shape that hasn't matched the actual `EventWebsitePage` queries
 * since PR-3 P1.6 renamed them to `["website", eventId, "<bucket>"]`.
 * Centralising here prevents the next renaming round from drifting.
 *
 * Keep this list in sync with the `useQuery` declarations in
 * `src/pages/event/EventWebsitePage.tsx` (around line 34-67). If a
 * new website-scoped query gets added there, add the same suffix
 * here.
 */
export function invalidateWebsiteKeys(queryClient: QueryClient, eventId: string): void {
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "exists"] });
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "event"] });
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "overview"] });
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "health"] });
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "subs"] });
  // Drives the guest-detail drawer's "Open guest website" link — see
  // src/lib/query/hooks/useWebsiteSummary.ts. Without this entry, publishing
  // or changing the custom domain wouldn't reflect in the drawer for 5 min.
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "summary"] });
  // The app builder's "Match your website" source (decompiled home page) —
  // see src/pages/builders/app/useWebsiteHomeBlocks.ts. Page edits/deletes
  // must refresh the import proposal, not serve a 5-min-stale home page.
  void queryClient.invalidateQueries({ queryKey: ["website", eventId, "home-app-source"] });
}
